import { Stack } from "@fluentui/react";
import {
  TagPicker,
  TagPickerList,
  TagPickerInput,
  TagPickerControl,
  TagPickerProps,
  TagPickerOption,
  TagPickerGroup,
  Tag,
  useTagPickerFilter,
} from "@fluentui/react-components";
import { styles } from "../ExperimentDetailsForm/experimentDetailsForm.styles";
import { FIELD_TOOLTIPS } from "../ExperimentDetailsForm/ExperimentDetailsValidation/experimentDetailsFormValidation";
import BaseLabelRenderer from "./BaseLabelRenderer";
import { useState } from "react";

interface BaseTagDropdownFieldProps {
  fieldKey: string;
  label: string;
  options: string[];
  selectedOptions: string[];
  isRequired?: boolean;
  isEdit: boolean;
  subtitle?: string | JSX.Element;
  placeholder?: string;
  errorMessage?: string[];
  onChange: (fieldKey: string, selectedOptions: string[]) => void;
  grow?: boolean;
  loading?: boolean;
  secondaryContentRenderer?: (option: string) => string;
}
const BaseTagDropdownField: React.FC<BaseTagDropdownFieldProps> = ({
  fieldKey,
  label,
  options,
  selectedOptions = [],
  isRequired = false,
  isEdit,
  subtitle,
  placeholder = "Select tag or type to add custom option...",
  errorMessage,
  onChange,
  grow = false,
  loading = false,
  secondaryContentRenderer,
}) => {
  const [searchString, setSearchString] = useState("");
  const hasTooltip = FIELD_TOOLTIPS[fieldKey as string];
  const onOptionSelect: TagPickerProps["onOptionSelect"] = (_, data) => {
    if (data.value === "loading") {
      return;
    }
    onChange(fieldKey as string, data.selectedOptions);
    setSearchString("");
  };

  const content = (
    <>
      <BaseLabelRenderer
        label={label}
        isRequired={isRequired}
        tooltip={hasTooltip}
        description={subtitle}
      />
      <TagPicker
        onOptionSelect={onOptionSelect}
        positioning="below"
        selectedOptions={selectedOptions}
        size="medium"
        disabled={!isEdit}
      >
        <TagPickerControl
          className={
            isEdit
              ? styles.experimentDetailsTagPickerDropdown
              : styles.experimentDetailsTagPickerDropdownDisabled
          }
        >
          <TagPickerGroup>
            {selectedOptions.map((option) => (
              <Tag key={option} value={option}>
                {option}
              </Tag>
            ))}
          </TagPickerGroup>
          <TagPickerInput
            data-testid={`${fieldKey}-input`}
            placeholder={placeholder}
            value={searchString}
            onChange={(e) => {
              setSearchString(e.target.value);
            }}
          />
        </TagPickerControl>
        <TagPickerList
          style={{
            maxHeight: "200px",
          }}
          className={styles.experimentDetailsTagPickerDropdownOption}
        >
          {useTagPickerFilter({
            query: searchString,
            options: options.filter(
              (option) => !selectedOptions.includes(option),
            ),
            noOptionsElement: (
              <TagPickerOption value={loading ? "loading" : searchString}>
                {loading
                  ? "Loading options..."
                  : `Press Enter to add "${searchString}"`}
              </TagPickerOption>
            ),
            renderOption: (option) => (
              <TagPickerOption
                key={option}
                value={option}
                secondaryContent={
                  secondaryContentRenderer?.(option) || undefined
                }
              >
                {option}
              </TagPickerOption>
            ),
          })}
        </TagPickerList>
      </TagPicker>
      {errorMessage && errorMessage.length > 0 && (
        <span className={styles.experimentDetailsError}>
          {errorMessage.join("; ")}
        </span>
      )}
    </>
  );

  if (grow) {
    return <Stack grow>{content}</Stack>;
  }

  return content;
};

export default BaseTagDropdownField;
